/**
 * Blocking ToS clickwrap gate.
 *
 * Renders a non-dismissible overlay (no X, no Escape, no click-outside) over
 * the entire app until the current user accepts the current TOS_VERSION.
 * Unchecked-by-default checkbox + explicit "Agree and continue" click =
 * enforceable clickwrap; the API logs version, verbatim text, IP, and
 * user-agent as evidence. Bumping TOS_VERSION in @servegrace/shared re-gates
 * every user automatically.
 *
 * Intentionally NOT the shared Modal component — that renders a close button
 * and closes on outside click, both of which would undermine the gate.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ScrollText } from 'lucide-react';
import { TOS_VERSION, LEGAL_URLS } from '@servegrace/shared';
import api from '../../lib/api';
import Button from '../ui/Button';

interface TosStatus {
  accepted: boolean;
  current_version: string;
  accepted_at: string | null;
}

export default function TosAcceptanceGate() {
  const [agreed, setAgreed] = useState(false);
  const queryClient = useQueryClient();

  const { data: status } = useQuery<TosStatus>({
    queryKey: ['tos-status'],
    queryFn: async () => (await api.get('/api/legal/tos-status')).data,
    staleTime: Infinity,
    retry: 2,
  });

  const acceptMutation = useMutation({
    mutationFn: async () => (await api.post('/api/legal/accept-tos', { agreed: true })).data,
    onSuccess: () => {
      queryClient.setQueryData(['tos-status'], (prev: TosStatus | undefined) => ({
        ...(prev ?? { current_version: TOS_VERSION }),
        accepted: true,
        accepted_at: new Date().toISOString(),
      }));
    },
  });

  // Fail open while loading/errored — never lock users out because the
  // status call hiccuped; the gate reappears on the next successful load.
  if (!status || status.accepted) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="tos-gate-title"
        className="w-full max-w-md bg-surface border border-border rounded-xl shadow-2xl p-6"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center shrink-0">
            <ScrollText size={20} className="text-accent" />
          </div>
          <h2 id="tos-gate-title" className="text-lg font-heading font-semibold text-text">
            Terms of Service
          </h2>
        </div>

        <p className="text-sm text-text-2 mb-4">
          Before continuing, please review and accept our updated Terms of Service
          (effective {status.current_version}).
        </p>

        <label className="flex items-start gap-3 p-3 rounded-lg border border-border bg-surface-2 cursor-pointer mb-4">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-border accent-[var(--accent,#6366f1)]"
          />
          <span className="text-sm text-text-2">
            I have read and agree to the ServeGrace{' '}
            <a
              href={LEGAL_URLS.terms}
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent underline hover:no-underline"
            >
              Terms of Service
            </a>{' '}
            (effective {status.current_version}) and acknowledge the{' '}
            <a
              href={LEGAL_URLS.privacy}
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent underline hover:no-underline"
            >
              Privacy Policy
            </a>
            .
          </span>
        </label>

        {acceptMutation.isError && (
          <p className="text-sm text-red-500 mb-3">
            Something went wrong saving your acceptance. Please try again.
          </p>
        )}

        <Button
          className="w-full"
          disabled={!agreed || acceptMutation.isPending}
          onClick={() => acceptMutation.mutate()}
        >
          {acceptMutation.isPending ? 'Saving…' : 'Agree and continue'}
        </Button>
      </div>
    </div>
  );
}
